import type { ReactElement } from 'react';
import { ArrowDown, ArrowUp, ArrowUpToLine, GripVertical } from 'lucide-react';

import type { ManualOrderControls } from '../hooks/useManualOrder';

interface DragHandleCellProps {
  /** The id of the row (drag source identification). */
  id: string;
  /** The human name in the aria labels of the nudge buttons. */
  label: string;
  controls: ManualOrderControls;
  /** A mutation of this row is in flight — nudge buttons pause. */
  busy?: boolean;
  className?: string;
  /**
   * F-28 («Все проекты», «Вручную» only): the row's «наверх» — the project
   * goes to GLOBAL position 1 of the full list. Lives here, next to the ↑/↓
   * nudges: it is an order operation, and the actions column is capped at
   * three buttons (STYLEGUIDE §5). Absent on the scoped queues (F-25) — they
   * have no global order to jump to.
   */
  onSendToTop?: (id: string) => void;
}

/**
 * The leading drag column of a manual-order row (F-25, shared by «Потоки»,
 * «Команды» and «Исполнители»): the ⠿ handle stays as the VISUAL affordance
 * only — the whole `<tr>` is the draggable (`useManualOrder`), so the button
 * itself is a no-op that must not steal the drag — plus ↑/↓ as the keyboard
 * alternative to the drag (STYLEGUIDE §7).
 */
export function DragHandleCell({ id, label, controls, busy = false, className, onSendToTop }: DragHandleCellProps): ReactElement {
  const rowIndex = controls.order.indexOf(id);

  if (!controls.enabled) {
    return (
      <td className={className}>
        <span className="project-row__drag-tools">
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            disabled
            title="Перетаскивание доступно при сортировке «Вручную»"
          >
            <GripVertical size={14} aria-hidden="true" />
          </button>
        </span>
      </td>
    );
  }

  return (
    <td className={className}>
      <span className="project-row__drag-tools">
        <button
          type="button"
          className="icon-btn icon-btn--sm project-row__drag-handle"
          aria-label="Перетащить, чтобы изменить порядок"
          title="Перетащите, чтобы изменить порядок"
          // The row carries the drag; this button is the affordance (and must
          // NOT be draggable — the row's guard skips drag starts on it).
          tabIndex={-1}
        >
          <GripVertical size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          aria-label={`Поднять выше: ${label}`}
          title="Поднять выше"
          disabled={busy || rowIndex <= 0}
          onClick={() => controls.onNudge(id, -1)}
        >
          <ArrowUp size={12} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          aria-label={`Опустить ниже: ${label}`}
          title="Опустить ниже"
          disabled={busy || rowIndex < 0 || rowIndex >= controls.order.length - 1}
          onClick={() => controls.onNudge(id, 1)}
        >
          <ArrowDown size={12} aria-hidden="true" />
        </button>
        {onSendToTop ? (
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            aria-label={`В самый верх: ${label}`}
            title="В самый верх"
            // Position 1 is already the top — a click would be a no-op (the
            // same edge rule as the ↑ nudge above).
            disabled={busy || rowIndex <= 0}
            onClick={() => onSendToTop(id)}
          >
            <ArrowUpToLine size={12} aria-hidden="true" />
          </button>
        ) : null}
      </span>
    </td>
  );
}
